/**
 * Slice Engine Animations
 * Lightweight scroll-triggered animations using IntersectionObserver.
 * Replaces GSAP scroll effects from the Hydrogen app.
 */

(function () {
  'use strict';

  var SELECTOR = '.animate-on-scroll';
  var VISIBLE_CLASS = 'is-visible';
  var THRESHOLD = 0.15;

  if (!('IntersectionObserver' in window)) {
    // Fallback: show all elements immediately
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      el.classList.add(VISIBLE_CLASS);
    });
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add(VISIBLE_CLASS);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: THRESHOLD }
  );

  // Observe elements present on page load
  document.querySelectorAll(SELECTOR).forEach(function (el) {
    observer.observe(el);
  });

  // Observe elements added dynamically (e.g., Shopify section rendering)
  var mutationObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(SELECTOR)) {
          observer.observe(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll(SELECTOR).forEach(function (el) {
            observer.observe(el);
          });
        }
      });
    });
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
})();

(function () {
  'use strict';

  var SELECTOR = '[data-karst-shop-hero]';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var rafId = 0;

  function sections() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  function resetSection(section) {
    section.style.setProperty('--karst-shop-hero-media-y', '0px');
    section.style.setProperty('--karst-shop-hero-text-y', '0px');
  }

  function update() {
    rafId = 0;

    if (reduceMotion) {
      sections().forEach(resetSection);
      return;
    }

    var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    sections().forEach(function (section) {
      var rect = section.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= viewportHeight) {
        resetSection(section);
        return;
      }

      section.style.setProperty('--karst-shop-hero-media-y', (-rect.top / 7) + 'px');
      section.style.setProperty('--karst-shop-hero-text-y', (-rect.top / 30) + 'px');
    });
  }

  function requestUpdate() {
    if (rafId) return;
    rafId = window.requestAnimationFrame(update);
  }

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
  document.addEventListener('shopify:section:load', requestUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', requestUpdate);
  } else {
    requestUpdate();
  }
})();

(function () {
  'use strict';

  var COUNTRY_SWITCH_STORAGE_KEY = 'karst:localization-country-switch';
  var COUNTRY_STORAGE_KEY = 'karst:localization-country';
  var CURRENT_COUNTRY = document.documentElement.getAttribute('data-localization-country') || '';

  // Separate localStorage key (same string, different Storage API) that records the
  // shopper's explicit country preference for assets/karst-country-popup.js -- unrelated
  // to the sessionStorage bfcache-reload check above.
  var LOCAL_COUNTRY_PREFERENCE_KEY = 'karst:localization-country';

  function persistLocalCountryPreference(countryCode) {
    try {
      if (window.localStorage && countryCode) {
        window.localStorage.setItem(LOCAL_COUNTRY_PREFERENCE_KEY, String(countryCode));
      }
    } catch (error) {}
  }

  function readStoredCountrySwitch() {
    try {
      var rawValue = window.sessionStorage && window.sessionStorage.getItem(COUNTRY_SWITCH_STORAGE_KEY);
      if (!rawValue) return null;

      var payload = JSON.parse(rawValue);
      if (!payload || !payload.submittedAt || Date.now() - payload.submittedAt > 5 * 60 * 1000) {
        window.sessionStorage.removeItem(COUNTRY_SWITCH_STORAGE_KEY);
        return null;
      }

      return payload;
    } catch (error) {
      return null;
    }
  }

  function clearStoredCountrySwitch() {
    try {
      if (window.sessionStorage) window.sessionStorage.removeItem(COUNTRY_SWITCH_STORAGE_KEY);
    } catch (error) {}
  }

  function readStoredCountry() {
    try {
      return window.sessionStorage && window.sessionStorage.getItem(COUNTRY_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function storeCurrentCountry(countryCode) {
    try {
      if (window.sessionStorage && countryCode) {
        window.sessionStorage.setItem(COUNTRY_STORAGE_KEY, String(countryCode));
      }
    } catch (error) {}
  }

  function storeCountrySwitch(form) {
    try {
      if (!window.sessionStorage || !window.FormData) return;
      var formData = new FormData(form);
      var countryCode = formData.get('country_code');
      if (!countryCode) return;

      window.sessionStorage.setItem(COUNTRY_SWITCH_STORAGE_KEY, JSON.stringify({
        countryCode: String(countryCode),
        path: window.location.pathname,
        submittedAt: Date.now()
      }));
      storeCurrentCountry(countryCode);
      persistLocalCountryPreference(countryCode);
    } catch (error) {}
  }

  function closeSelector(selector) {
    var toggle = selector.querySelector('[data-localization-toggle]');
    var dropdown = selector.querySelector('[data-localization-dropdown]');

    selector.removeAttribute('data-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (dropdown) dropdown.hidden = true;
  }

  function closeAllSelectors(exceptSelector) {
    document.querySelectorAll('[data-localization-selector]').forEach(function (selector) {
      if (selector !== exceptSelector) closeSelector(selector);
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var toggle = target.closest && target.closest('[data-localization-toggle]');

    if (toggle) {
      var selector = toggle.closest('[data-localization-selector]');
      var dropdown = selector && selector.querySelector('[data-localization-dropdown]');
      var willOpen = selector && selector.getAttribute('data-open') !== 'true';

      event.preventDefault();
      if (!selector || !dropdown) return;

      closeAllSelectors(selector);
      if (willOpen) {
        selector.setAttribute('data-open', 'true');
      } else {
        selector.removeAttribute('data-open');
      }
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      dropdown.hidden = !willOpen;
      return;
    }

    if (!target.closest || !target.closest('[data-localization-selector]')) {
      closeAllSelectors(null);
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.karst-localization__form');
    var selector = form && form.closest('[data-localization-selector]');

    if (form) storeCountrySwitch(form);
    if (selector) selector.classList.add('is-disabled');
  }, true);

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    var storedCountry = readStoredCountry();
    if (storedCountry && CURRENT_COUNTRY && storedCountry !== CURRENT_COUNTRY) {
      window.location.reload();
      return;
    }

    if (!readStoredCountrySwitch()) return;
    clearStoredCountrySwitch();
    window.location.reload();
  });

  if (!readStoredCountrySwitch()) storeCurrentCountry(CURRENT_COUNTRY);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    closeAllSelectors(null);
  });
})();
